import { downloadFileUsingUrl } from '@util/downloadFile';
import { Button, Icon, IconButton, notify, ProgressBar } from '@yembo/yemblocks-core';
import { useContext, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { useGetExportJobsData } from 'src/queries/ExportRequest';
import { EXPORT_REQUEST_KEY, ExportRequestContext } from '../Context/ExportRequest/context';
import { useGenerateJobsCSV } from '../Hooks/useGenerateJobsCSV';

export const GenerateJobsCSVNotification = () => {
  const {
    exportRequestKey,
    isExportFailed,
    setIsExportFailed,
    setExportRequestKey,
    setIsConcurrencyError,
    isConcurrencyError,
  } = useContext(ExportRequestContext);

  const { exportJobsCSV, isInitiatingExport } = useGenerateJobsCSV();

  const draggableRef = useRef<HTMLDivElement>(null);

  const { data: exportJobsCSVData } = useGetExportJobsData(exportRequestKey ?? '', {
    enabled: !!exportRequestKey,
    refetchInterval: ({ state: { data } }) => {
      const { status = '' } = data ?? {};

      const isExportFinished = ['completed', 'failed'].includes(status);

      if (status === 'failed') setIsExportFailed(true);

      return isExportFinished ? false : 1000;
    },
  });

  const { status = '', progressPercentage = 0, url } = exportJobsCSVData ?? {};

  useEffect(() => {
    if (isExportFailed) notify.error('CSV export failed!');
  }, [isExportFailed]);

  const isNotificationVisible = !!exportRequestKey || !!isExportFailed;

  const getWindowContent = () => {
    if (isExportFailed) {
      const bodyText = isConcurrencyError
        ? "Due to high demand, we're unable to complete your request right now. Please try again in a few minutes."
        : 'Something went wrong while generating your file. Please try again.';

      return {
        headerText: 'CSV Export Failed',
        bodyText,
      };
    }

    if (status === 'completed')
      return {
        headerText: 'Your file is ready!',
        bodyText: 'Click “Download CSV” to save it to your computer.',
      };

    return {
      headerText: 'Generating CSV',
      bodyText:
        'We’re creating your CSV with the filters you applied. You can keep working; we’ll let you know when it’s ready.',
    };
  };

  const { bodyText, headerText } = getWindowContent();

  const clearExportRequestData = () => {
    setExportRequestKey(null);
    localStorage.removeItem(EXPORT_REQUEST_KEY);
    setIsExportFailed(false);
    setIsConcurrencyError(false);
  };

  const shouldShowCloseButton = isExportFailed || status === 'completed';

  return (
    isNotificationVisible && (
      <Draggable
        handle='.generate-jobs-csv-notification'
        bounds='parent'
        nodeRef={draggableRef}
        cancel='.close-button, .action-button'
      >
        <div className='generate-jobs-csv-notification' ref={draggableRef}>
          <div className='subtitle-1'>{headerText}</div>

          <div>{bodyText}</div>

          {(['pending', 'not_started'].includes(status) || (!exportJobsCSVData && !isExportFailed)) && (
            <ProgressBar valueCurrent={progressPercentage} valueMax={100} shouldShowPercentage />
          )}

          {status === 'completed' && (
            <Button
              className='action-button'
              text='Download CSV'
              icon={<Icon name='download' />}
              onClick={() => {
                if (!url) return;

                downloadFileUsingUrl(url);

                notify.success('CSV downloaded!');

                clearExportRequestData();
              }}
            />
          )}

          {shouldShowCloseButton && (
            <IconButton
              className='close-button'
              kind='flat'
              size='large'
              icon={<Icon name='x' />}
              onClick={clearExportRequestData}
            />
          )}

          {isExportFailed && !isConcurrencyError && (
            <Button isLoading={isInitiatingExport} text='Retry' className='action-button' onClick={exportJobsCSV} />
          )}
        </div>
      </Draggable>
    )
  );
};
